import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ListTodo, Loader2, AlertTriangle, Clock, CalendarClock } from 'lucide-react';
import { crmFollowupsAPI } from '@/lib/api';
import { FollowupDetailDrawer } from './FollowupDetailDrawer';
import { formatDate } from '@/lib/utils';

/**
 * FollowupQueueWidget — extended dashboard queue for the unified follow-up
 * system. Three tabs: Due Today, Due Soon, Overdue.
 * Row click opens the FollowupDetailDrawer.
 *
 * Phase 27.30.9 — deep-link: notifications that fall back to /dashboard
 * carry `?followup={id}` in the URL. When present we switch to the
 * matching tab, scroll the row into view, add a highlight pulse, and
 * open the detail drawer. Then we clear the query param so a manual
 * refresh doesn't reopen the drawer.
 */
export function FollowupQueueWidget() {
  const [summary, setSummary] = useState({ today: 0, due_soon: 0, overdue: 0, completed: 0, open_total: 0 });
  const [byTab, setByTab] = useState({ today: [], soon: [], overdue: [] });
  const [tab, setTab] = useState('today');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const rowRefs = useRef({});
  const consumedRef = useRef(false);
  const cardRef = useRef(null);
  const windowConsumedRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, today, soon, overdue] = await Promise.all([
        crmFollowupsAPI.summary().then((r) => r.data).catch(() => ({})),
        crmFollowupsAPI.list({ window: 'today', page_size: 25 }).then((r) => r.data.items || []).catch(() => []),
        crmFollowupsAPI.list({ window: 'soon', page_size: 25 }).then((r) => r.data.items || []).catch(() => []),
        crmFollowupsAPI.list({ window: 'overdue', page_size: 25 }).then((r) => r.data.items || []).catch(() => []),
      ]);
      setSummary(s || {});
      setByTab({ today, soon, overdue });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Phase 27.30.9 — after the queue loads, honour `?followup={id}`.
  useEffect(() => {
    if (loading || consumedRef.current) return;
    const params = new URLSearchParams(location.search);
    const target = params.get('followup');
    if (!target) return;
    const inTab =
      byTab.today.some((f) => f.followup_id === target) ? 'today' :
      byTab.soon.some((f) => f.followup_id === target) ? 'soon' :
      byTab.overdue.some((f) => f.followup_id === target) ? 'overdue' : null;
    if (!inTab) return;
    consumedRef.current = true;
    setTab(inTab);
    setHighlightId(target);
    // Wait one frame for the tab content to render the row.
    setTimeout(() => {
      const el = rowRefs.current[target];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setOpenId(target);
    }, 60);
    // Clear the query param so a refresh doesn't reopen the drawer.
    const next = new URLSearchParams(location.search);
    next.delete('followup');
    navigate(
      { pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : '' },
      { replace: true },
    );
    // Fade out the highlight after 3 s.
    setTimeout(() => setHighlightId(null), 3000);
  }, [loading, byTab, location.search, location.pathname, navigate]);

  // Deep-link from the dashboard ActionCenter "Overdue follow-ups" / "Due
  // soon" tiles: `?followup_window=overdue|soon|today` selects the matching
  // tab and scrolls the widget into view. Cleared afterwards so a manual
  // refresh doesn't re-trigger.
  useEffect(() => {
    if (windowConsumedRef.current) return;
    const params = new URLSearchParams(location.search);
    const win = params.get('followup_window');
    if (!win || !['today', 'soon', 'overdue'].includes(win)) return;
    windowConsumedRef.current = true;
    setTab(win);
    setTimeout(() => {
      if (cardRef.current?.scrollIntoView) {
        cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
    const next = new URLSearchParams(location.search);
    next.delete('followup_window');
    navigate(
      { pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : '' },
      { replace: true },
    );
  }, [location.search, location.pathname, navigate]);

  const rowsFor = (t) => (t === 'today' ? byTab.today : t === 'soon' ? byTab.soon : byTab.overdue);
  const emptyLabel = (t) => (t === 'today' ? 'Nothing due today.' : t === 'soon' ? 'Nothing due soon.' : 'No overdue follow-ups.');

  return (
    <Card data-testid="followup-queue-widget" ref={cardRef}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <ListTodo size={16} className="text-slate-500" />
            My Follow-ups
          </CardTitle>
          <div className="text-xs text-slate-500">
            Open: <b>{summary.open_total || 0}</b>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 mb-3">
            <TabsTrigger value="today" data-testid="followup-tab-today">
              <CalendarClock size={12} className="mr-1" /> Today
              {summary.today ? <Badge className="ml-1 bg-slate-200 text-slate-800">{summary.today}</Badge> : null}
            </TabsTrigger>
            <TabsTrigger value="soon" data-testid="followup-tab-soon">
              <Clock size={12} className="mr-1" /> Due Soon
              {summary.due_soon ? <Badge className="ml-1 bg-amber-100 text-amber-800">{summary.due_soon}</Badge> : null}
            </TabsTrigger>
            <TabsTrigger value="overdue" data-testid="followup-tab-overdue">
              <AlertTriangle size={12} className="mr-1" /> Overdue
              {summary.overdue ? <Badge className="ml-1 bg-red-100 text-red-800">{summary.overdue}</Badge> : null}
            </TabsTrigger>
          </TabsList>

          {['today', 'soon', 'overdue'].map((t) => (
            <TabsContent key={t} value={t}>
              {loading ? (
                <div className="py-6 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
              ) : rowsFor(t).length === 0 ? (
                <div className="py-6 text-center text-sm text-slate-500">{emptyLabel(t)}</div>
              ) : (
                <ul className="divide-y divide-slate-200">
                  {rowsFor(t).map((fu) => {
                    const isHighlighted = highlightId === fu.followup_id;
                    return (
                      <li
                        key={fu.followup_id}
                        ref={(el) => { if (el) rowRefs.current[fu.followup_id] = el; }}
                        className={`py-2 -mx-2 px-2 rounded transition-colors duration-500 ${
                          isHighlighted ? 'bg-sky-100 ring-2 ring-sky-400 animate-pulse' : ''
                        }`}
                        data-testid={`followup-li-${fu.followup_id}`}
                      >
                        <button
                          onClick={() => setOpenId(fu.followup_id)}
                          className="w-full text-left flex items-start justify-between gap-2 hover:bg-slate-50 px-2 py-1 rounded"
                          data-testid={`followup-row-${fu.followup_id}`}
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-800 truncate">{fu.title}</div>
                            <div className="text-xs text-slate-500">
                              Due {fu.due_date || '—'} · {(fu.assignees || []).length} assignee(s)
                            </div>
                          </div>
                          <Badge className={
                            fu.status === 'overdue' ? 'bg-red-100 text-red-800' :
                            fu.status === 'due_soon' ? 'bg-amber-100 text-amber-800' :
                            fu.status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {fu.status.replace('_', ' ')}
                          </Badge>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>

      <FollowupDetailDrawer
        open={!!openId}
        onOpenChange={(v) => { if (!v) setOpenId(null); }}
        followupId={openId}
        onChanged={load}
      />
    </Card>
  );
}

export default FollowupQueueWidget;
